import queryParamsFromObject from '../../Client/modules/converter/queryParamsFromObject.js';

const REGO_MESSAGES = {
  notFound: 'Could not find a vehicle for this registration.',
  notResolved: 'Could not identify the vehicle for this registration.',
  noParts: (vehicle) => `No parts found for ${vehicle}.`,
};

const REGO_INPUT_SELECTOR = '.cm_rego-search-input, .vehicle-search-modal__rego-input';
const STATE_SELECT_SELECTOR = '[data-field="state"], .vehicle-search-modal__select-input';

function setRegoSearchLoading(button, loading) {
  if (!button) {
    return;
  }

  button.classList.toggle('cm_spinner', loading);
  button.disabled = loading;
}

function getSearchEndpoint() {
  return window.Convermax?.templates?.config?.requestConfig?.serverUrl;
}

function getRegoSearchContext(event) {
  const dialogPanel = event.target.closest('.vehicle-search-modal__panel');
  if (dialogPanel) {
    return {
      root: dialogPanel,
      regoInput: dialogPanel.querySelector(REGO_INPUT_SELECTOR),
      stateSelect: dialogPanel.querySelector(STATE_SELECT_SELECTOR),
      isDialog: true,
    };
  }

  const tabRoot = event.target.closest('.cm_rego-search');
  if (!tabRoot) {
    return null;
  }

  return {
    root: tabRoot,
    regoInput: tabRoot.querySelector(REGO_INPUT_SELECTOR),
    stateSelect: tabRoot.querySelector(STATE_SELECT_SELECTOR),
    isDialog: false,
  };
}

function applyRegoVehicle(vehicle) {
  window.Convermax.setVehicle(vehicle);
  window.location.assign(window.Convermax.getVehicleUrl('/pages/search'));
}

function addMessage(context, message) {
  const { root, regoInput } = context;
  if (!regoInput) {
    return;
  }

  root.querySelector('.cm-rego-message')?.remove();

  const msg = document.createElement('div');
  msg.className = 'cm-rego-message';
  msg.textContent = message;

  const anchor = root.querySelector(
    '.cm_rego-search-form, .vehicle-search-modal__search-button, .vehicle-search-modal__rego',
  );
  anchor?.insertAdjacentElement('afterend', msg);

  requestAnimationFrame(() => {
    msg.style.opacity = '1';
  });

  regoInput.addEventListener(
    'input',
    () => {
      msg.style.opacity = '0';
      setTimeout(() => msg.remove(), 300);
    },
    { once: true },
  );
}

function parseVehicleFromFacets(facets, fields) {
  const fieldSet = new Set(fields);

  return facets.reduce(
    (acc, facet) => {
      if (!fieldSet.has(facet.FieldName)) {
        return acc;
      }
      if (facet.Selection?.length) {
        acc.vehicle[facet.FieldName] = facet.Selection[0].Value;
      } else {
        acc.missingFields.push(facet.FieldName);
      }
      return acc;
    },
    { vehicle: {}, missingFields: [] },
  );
}

export async function regoSearch(event, fields, baseFields) {
  const context = getRegoSearchContext(event);
  if (!context) {
    return;
  }

  const { regoInput, stateSelect } = context;
  const state = stateSelect?.value?.trim();
  const rego = regoInput?.value?.trim();

  if (!state || !rego) {
    return;
  }

  const searchEndpoint = getSearchEndpoint();
  if (!searchEndpoint) {
    return;
  }

  const submitButton = event.target.closest('.cm_rego-search-button, .vehicle-search-modal__search-button');

  try {
    setRegoSearchLoading(submitButton, true);

    const queryParams = queryParamsFromObject({
      query: `state:${state};rego:${rego}`,
      pageSize: 0,
    });
    const url = `${searchEndpoint}/search.json?${queryParams}`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Search error: ${response.status} ${response.statusText}`);
    }

    const { Facets: facets = [], Messages: messages = [], Extra: extra = {} } = await response.json();

    if (messages.includes('RegoNotFound')) {
      addMessage(context, REGO_MESSAGES.notFound);
      return;
    }

    if (messages.includes('RegoVehicleNotResolved')) {
      addMessage(context, REGO_MESSAGES.notResolved);
      return;
    }

    if (messages.includes('RegoNoParts') && extra.regoSearchParsedVehicle) {
      addMessage(context, REGO_MESSAGES.noParts(extra.regoSearchParsedVehicle));
      return;
    }

    const { vehicle, missingFields } = parseVehicleFromFacets(facets, fields);
    const regoSearchParsedVehicle = extra.regoSearchParsedVehicle;

    // if any base field is missing the vehicle cannot be selected yet
    // Base fields may not be found in facets when no products with matching fitments exist
    // in that case, treat it the same as "no parts" rather than switching to manual UI.
    const baseFieldSet = new Set(baseFields);
    const hasMissingBaseFields = missingFields.some((f) => baseFieldSet.has(f));
    if (hasMissingBaseFields) {
      addMessage(
        context,
        regoSearchParsedVehicle ? REGO_MESSAGES.noParts(regoSearchParsedVehicle) : REGO_MESSAGES.notResolved,
      );
      return;
    }

    // all base fields resolved. extra fields may still be missing (different Series across kTypes etc.)
    if (Object.keys(vehicle).length > 0) {
      try {
        applyRegoVehicle(vehicle);
      } catch {
        addMessage(
          context,
          regoSearchParsedVehicle
            ? REGO_MESSAGES.noParts(regoSearchParsedVehicle)
            : REGO_MESSAGES.notResolved,
        );
      }
    }
  } catch (error) {
    console.error('Rego search failed:', error);
  } finally {
    setRegoSearchLoading(submitButton, false);
  }
}
